import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Form as FinalForm, Field } from 'react-final-form';

import { useConfiguration } from '../../../../context/configurationContext';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../util/routes';
import { parse } from '../../../../util/urlHelpers';
import { isOriginInUse, getDefaultSearchOrigin } from '../../../../util/search';
import { types as sdkTypes } from '../../../../util/sdkLoader';
import { Form, FieldLocationAutocompleteInput } from '../../../../components';
import * as geocoderGoogleMaps from '../../../../components/LocationAutocompleteInput/GeocoderGoogleMaps';
import * as geocoderMapbox from '../../../../components/LocationAutocompleteInput/GeocoderMapbox';

import css from './TopbarSearchPanel.module.css';

const { LatLng } = sdkTypes;
const identity = v => v;

// Same geocoder resolution as LocationAutocompleteInputImpl -- used to resolve a typed-but-not-picked
// location into a place (address/bounds/origin) when the Search button submits the form.
const getGeocoderVariant = mapProvider =>
  mapProvider === 'googleMaps' ? geocoderGoogleMaps : geocoderMapbox;

// KidHive search pill (Figma 0-6785 / filter popover 0-9446). Self-contained: it reads config/history
// itself and submits to SearchPage, so it can be dropped into the desktop topbar AND the mobile
// search modal. Keyword search submits real `keywords`; the filter button opens a popover with
// category chips + a child-age bucket that apply real search params. Location is a real autocomplete
// emitting address/bounds/origin (origin drives the distance-default sort + card distances), prefilled
// from the signed-in parent's saved onboarding location.

const IconSearch = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="9" cy="9" r="6.25" stroke="currentColor" strokeWidth="1.6" />
    <path d="M13.6 13.6 17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IconFilter = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M3 6h10M3 14h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="15" cy="6" r="2" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="11" cy="14" r="2" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);
const IconPin = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M10 18s6-5.2 6-9.5A6 6 0 0 0 4 8.5C4 12.8 10 18 10 18Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="10" cy="8.5" r="2.2" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

// Child-age range buckets for the filter. The listing's age band is a NUMERIC field (ageBandMin/
// ageBandMax), so these ranges are a UI grouping — they can't be derived from a number field the way
// enum options can. Selecting a bucket runs an overlap query against the class's own age band.
const AGE_BUCKETS = [
  { key: '0-2', label: '0–2 yrs', min: 0, max: 2 },
  { key: '3-5', label: '3–5 yrs', min: 3, max: 5 },
  { key: '6-8', label: '6–8 yrs', min: 6, max: 8 },
  { key: '9-11', label: '9–11 yrs', min: 9, max: 11 },
  { key: '12-14', label: '12–14 yrs', min: 12, max: 14 },
  { key: '15-18', label: '15–18 yrs', min: 15, max: 18 },
];

// Days-of-week + class-format + price options for the quick-filter popover (parity with the
// desktop rail's cards). Values match the search schema params the rail writes.
const DAY_OPTIONS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];
const FORMAT_OPTIONS = [
  { key: 'single-session', label: 'Single session' },
  { key: 'term-enrollment', label: 'Multi-week term' },
];
// price=min,max in subunits (the stock price filter's URL encoding; 50000 = $500 cap).
const PRICE_OPTIONS = [
  { key: '0,2500', label: 'Under $25' },
  { key: '0,5000', label: 'Under $50' },
  { key: '0,10000', label: 'Under $100' },
  { key: '10000,50000', label: '$100+' },
];
const AGE_BUCKET_MAX = AGE_BUCKETS[AGE_BUCKETS.length - 1].max;

// Categories from the hosted-asset config (same resolver as the homepage categories section):
// built-in categories -> pub_categoryLevel1, else the custom `category` field -> pub_category.
const resolveCategories = config => {
  const builtIn = config?.categoryConfiguration?.categories || [];
  if (builtIn.length > 0) {
    return { param: 'pub_categoryLevel1', items: builtIn.map(c => ({ key: c.id, label: c.name })) };
  }
  const fields = config?.listing?.listingFields || [];
  const categoryField = fields.find(f => f.key === 'category');
  const options = categoryField?.enumOptions || [];
  return { param: 'pub_category', items: options.map(o => ({ key: o.option, label: o.label })) };
};

// Per-category symbol shown in front of the filter-popup chips (Figma 0-9446). Keyed by our real
// category slugs (the Figma's own taxonomy differs); a key with no emoji simply renders no symbol.
// Keyed by the hosted category id (config.categoryConfiguration.categories[].id). Previously keyed by
// stale slugs (sports/music/stem/...), so only 'arts-and-crafts' happened to match -- every other
// category rendered without an icon. Keep in sync with the listing-categories.json asset.
const CATEGORY_EMOJI = {
  'sports-and-fitness': '⚽',
  'arts-and-crafts': '🎨',
  'music-and-dance': '🎵',
  'stem-and-coding': '🔬',
  'language-learning': '🌍',
  'life-skills': '🧠',
  'team-sports': '🏀',
  'academic-tutor': '📚',
  'special-needs': '🧩',
  'mommy-and-me': '👶',
};

/**
 * KidHive topbar search pill + filter popover. Self-contained; render it in the desktop topbar or the
 * mobile search modal.
 * @component
 * @param {Object} props
 * @param {string?} props.className extra classes for the form root
 */
const TopbarSearchPanel = props => {
  const { className } = props;
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const currentUser = useSelector(state => state.user?.currentUser);
  const { param: categoryParam, items: categoryItems } = resolveCategories(config);

  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedAge, setSelectedAge] = useState('');
  // Parity with the desktop rail (client: mobile filtering only offered categories + age): days of
  // week, class format, and price live here too. Dates + distance stay on the search page's full
  // Filters modal (they need the calendar / origin math).
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedFormats, setSelectedFormats] = useState([]);
  const [priceRange, setPriceRange] = useState('');

  // Reflect the APPLIED filters (which live in the URL) in the panel. The topbar persists across
  // navigations, so without this the pill would still show an empty selection after redirecting to
  // search (or after Apply) even though the results are filtered. Re-syncs whenever the query changes
  // (Apply, a keyword search, back/forward, landing on /s with params). The URL is the source of truth.
  useEffect(() => {
    const params = parse(location.search);
    const cats = params[categoryParam];
    setSelectedCategories(typeof cats === 'string' && cats ? cats.split(',') : []);

    // Reverse the age overlap encoding: pub_ageBandMin='0,{bucket.max}', pub_ageBandMax='{bucket.min},{MAX}'.
    const minParam = typeof params.pub_ageBandMax === 'string' ? params.pub_ageBandMax : '';
    const maxParam = typeof params.pub_ageBandMin === 'string' ? params.pub_ageBandMin : '';
    const bucketMin = Number(minParam.split(',')[0]);
    const bucketMax = Number(maxParam.split(',')[1]);
    const bucket = AGE_BUCKETS.find(b => b.min === bucketMin && b.max === bucketMax);
    setSelectedAge(bucket ? bucket.key : '');

    const days = typeof params.pub_scheduleDays === 'string' && params.pub_scheduleDays
      ? params.pub_scheduleDays.split(',')
      : [];
    setSelectedDays(days);
    const formats = typeof params.pub_classFormat === 'string' && params.pub_classFormat
      ? params.pub_classFormat.split(',')
      : [];
    setSelectedFormats(formats);
    setPriceRange(typeof params.price === 'string' ? params.price : '');
  }, [location.search, categoryParam]);

  // Prefill: the URL is the source of truth -- when it carries a location (address/origin), the
  // field shows THAT (whatever the user last searched). Only when the URL has no location does the
  // signed-in parent's saved onboarding location prefill the field. Memoized by user id (not the
  // URL) so Final Form doesn't reinitialize under the user's typing; the panel remounts on page
  // navigation, which re-reads the URL exactly when it can have changed.
  const initialValues = useMemo(() => {
    const params = parse(location.search, { latlng: ['origin'], latlngBounds: ['bounds'] });
    const keywordsMaybe =
      typeof params.keywords === 'string' && params.keywords ? { keywords: params.keywords } : {};

    if (params.address || params.origin) {
      return {
        ...keywordsMaybe,
        location: {
          search: params.address || '',
          predictions: [],
          selectedPlace: {
            address: params.address || '',
            ...(params.origin ? { origin: params.origin } : {}),
            ...(params.bounds ? { bounds: params.bounds } : {}),
          },
        },
      };
    }

    const saved = getDefaultSearchOrigin(currentUser);
    return saved
      ? {
          ...keywordsMaybe,
          location: {
            search: saved.address || '',
            predictions: [],
            selectedPlace: {
              address: saved.address || '',
              origin: new LatLng(saved.lat, saved.lng),
            },
          },
        }
      : keywordsMaybe;
    // The URL is read fresh on recompute but is deliberately NOT a dependency: reinitializing on
    // every query change would reset the form mid-typing (map moves and filters push new URLs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id?.uuid]);

  // The query keys this panel's filter popover manages (so it can replace exactly those on Apply while
  // leaving keyword/location/sort untouched).
  const FILTER_KEYS = [
    categoryParam,
    'pub_ageBandMin',
    'pub_ageBandMax',
    'pub_scheduleDays',
    'pub_classFormat',
    'price',
  ];
  // The query keys the keyword+location search manages. mapSearch belongs to this group: it marks
  // "the map moved" URLs, and carrying it into a fresh address search would make the search-page map
  // skip re-fitting to the new location (it thinks the map itself produced the URL).
  const LOCATION_KEYS = ['keywords', 'address', 'bounds', 'origin', 'mapSearch'];

  const goToSearch = searchParams => {
    setFilterOpen(false);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };
  const omitKeys = (obj, keys) =>
    Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k)));

  // Search submits keywords + the selected place: address (display), bounds (the geo filter), and
  // origin (proximity sort + card distances) when origin-based search is on. It PRESERVES any applied
  // filters (and other params) already in the URL, only replacing the keyword/location keys.
  const buildLocationParams = (place, address) => ({
    address,
    ...(place.bounds ? { bounds: place.bounds } : {}),
    ...(isOriginInUse(config) && place.origin ? { origin: place.origin } : {}),
  });
  const handleSearchSubmit = values => {
    const kw = (values?.keywords || '').trim();
    const kwMaybe = kw ? { keywords: kw } : {};
    // Drop the URL-driven topbar modal flags too: submitting the mobile search panel must land on
    // the results with the panel CLOSED, not carry mobilesearch=open into the search URL.
    const preserved = omitKeys(parse(location.search), [
      ...LOCATION_KEYS,
      'page',
      'mobilesearch',
      'mobilemenu',
    ]);

    const selectedPlace = values?.location?.selectedPlace;
    const searchText = (values?.location?.search || '').trim();
    const predictions = values?.location?.predictions || [];

    if (selectedPlace) {
      goToSearch({ ...preserved, ...kwMaybe, ...buildLocationParams(selectedPlace, searchText) });
    } else if (searchText && predictions.length > 0) {
      // Typed but not picked from the list: resolve the top prediction (same as pressing Enter in
      // the field) so the typed location is what gets searched -- NOT silently dropped.
      const Geocoder = getGeocoderVariant(config.maps.mapProvider).default;
      const geocoder = new Geocoder();
      geocoder
        .getPlaceDetails(predictions[0], config.maps?.search?.currentLocationBoundsDistance)
        .then(place => {
          goToSearch({ ...preserved, ...kwMaybe, ...buildLocationParams(place, place.address) });
        })
        .catch(() => {
          // Geocoding failed: search without a location rather than blocking the search.
          goToSearch({ ...preserved, ...kwMaybe });
        });
    } else {
      // No location input at all: keyword-only (or empty) search, no location params.
      goToSearch({ ...preserved, ...kwMaybe });
    }
  };
  const toggleCategory = key =>
    setSelectedCategories(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  // Apply replaces exactly the panel's filter keys with the current selection, preserving keyword,
  // location and everything else already in the URL (so filtering from the search page doesn't drop
  // the keyword/location the results were found with).
  const handleApplyFilters = () => {
    const params = omitKeys(parse(location.search), [...FILTER_KEYS, 'page']);
    if (selectedCategories.length > 0) {
      params[categoryParam] = selectedCategories.join(',');
    }
    const ageBucket = AGE_BUCKETS.find(b => b.key === selectedAge);
    if (ageBucket) {
      // Classes whose own age band overlaps the selected bucket [min, max]:
      // ageBandMin <= bucket.max AND ageBandMax >= bucket.min.
      params.pub_ageBandMin = `0,${ageBucket.max}`;
      params.pub_ageBandMax = `${ageBucket.min},${AGE_BUCKET_MAX}`;
    }
    if (selectedDays.length > 0) {
      params.pub_scheduleDays = selectedDays.join(',');
    }
    if (selectedFormats.length > 0) {
      params.pub_classFormat = selectedFormats.join(',');
    }
    if (priceRange) {
      params.price = priceRange;
    }
    goToSearch(params);
  };
  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedAge('');
    setSelectedDays([]);
    setSelectedFormats([]);
    setPriceRange('');
  };
  const toggleDay = key =>
    setSelectedDays(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  const toggleFormat = key =>
    setSelectedFormats(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  const filterCount =
    selectedCategories.length +
    (selectedAge !== '' ? 1 : 0) +
    (selectedDays.length > 0 ? 1 : 0) +
    (selectedFormats.length > 0 ? 1 : 0) +
    (priceRange ? 1 : 0);

  return (
    <FinalForm
      onSubmit={handleSearchSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, values }) => (
        <Form
          className={classNames(css.searchPanel, className)}
          onSubmit={handleSubmit}
          role="search"
          enforcePagePreloadFor="SearchPage"
        >
          <div className={css.searchField}>
            <IconSearch className={css.searchFieldIcon} />
            <Field
              name="keywords"
              render={({ input }) => (
                <input
                  {...input}
                  className={classNames(css.searchInput, { [css.searchInputActive]: filterOpen })}
                  type="text"
                  placeholder="Search Activities"
                  aria-label="Search activities"
                  autoComplete="off"
                />
              )}
            />
            <div className={css.filterWrapper}>
              <button
                type="button"
                className={classNames(css.filterButton, { [css.filterButtonActive]: filterCount > 0 })}
                aria-label="Filters"
                aria-expanded={filterOpen}
                onClick={() => setFilterOpen(open => !open)}
              >
                <IconFilter className={css.filterIcon} />
                {filterCount > 0 ? <span className={css.filterBadge}>{filterCount}</span> : null}
              </button>
            </div>
          </div>
          {/* The popup anchors to the whole search pill (not the filter button's wrapper): inside
              the narrow mobile search modal a button-anchored 340px popup would run off the screen;
              spanning the pill keeps it inside on every width. */}
          {filterOpen ? (
            <>
              <button
                type="button"
                className={css.filterBackdrop}
                aria-hidden="true"
                tabIndex={-1}
                onClick={() => setFilterOpen(false)}
              />
              <div className={css.filterPopup}>
                    {categoryItems.length > 0 ? (
                      <div className={css.filterGroup}>
                        <span className={css.filterLabel}>Categories</span>
                        <div className={css.filterChips}>
                          {categoryItems.map(c => (
                            <button
                              key={c.key}
                              type="button"
                              className={classNames(css.chip, {
                                [css.chipActive]: selectedCategories.includes(c.key),
                              })}
                              onClick={() => toggleCategory(c.key)}
                            >
                              {CATEGORY_EMOJI[c.key] ? (
                                <span className={css.chipEmoji} aria-hidden="true">
                                  {CATEGORY_EMOJI[c.key]}
                                </span>
                              ) : null}
                              {c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {/* From medium up the popup is wider and pairs these groups side by side
                        (client ask: a shorter dropdown); on phones each row stacks. */}
                    <div className={css.filterRow}>
                      <div className={css.filterGroup}>
                        <span className={css.filterLabel}>Child age</span>
                        <div className={css.ageRow}>
                          <select
                            className={css.ageSelect}
                            value={selectedAge}
                            onChange={e => setSelectedAge(e.target.value)}
                            aria-label="Select your child age"
                          >
                            <option value="">Select your child age</option>
                            {AGE_BUCKETS.map(b => (
                              <option key={b.key} value={b.key}>
                                {b.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className={css.filterGroup}>
                        <span className={css.filterLabel}>Price</span>
                        <div className={css.ageRow}>
                          <select
                            className={css.ageSelect}
                            value={priceRange}
                            onChange={e => setPriceRange(e.target.value)}
                            aria-label="Select a price range"
                          >
                            <option value="">Any price</option>
                            {PRICE_OPTIONS.map(pr => (
                              <option key={pr.key} value={pr.key}>
                                {pr.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className={css.filterRow}>
                      <div className={css.filterGroup}>
                        <span className={css.filterLabel}>Days of week</span>
                        <div className={css.filterChips}>
                          {DAY_OPTIONS.map(d => (
                            <button
                              key={d.key}
                              type="button"
                              className={classNames(css.chip, {
                                [css.chipActive]: selectedDays.includes(d.key),
                              })}
                              onClick={() => toggleDay(d.key)}
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className={css.filterGroup}>
                        <span className={css.filterLabel}>Class format</span>
                        <div className={css.filterChips}>
                          {FORMAT_OPTIONS.map(f => (
                            <button
                              key={f.key}
                              type="button"
                              className={classNames(css.chip, {
                                [css.chipActive]: selectedFormats.includes(f.key),
                              })}
                              onClick={() => toggleFormat(f.key)}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                <div className={css.filterActions}>
                  <button type="button" className={css.clearButton} onClick={clearFilters}>
                    Clear
                  </button>
                  <button type="button" className={css.applyButton} onClick={handleApplyFilters}>
                    Apply
                  </button>
                </div>
              </div>
            </>
          ) : null}
          <div className={css.locationField}>
            <IconPin className={css.locationIcon} />
            <FieldLocationAutocompleteInput
              rootClassName={css.locationAutocompleteRoot}
              iconClassName={css.locationIconHidden}
              inputClassName={css.locationInput}
              predictionsClassName={css.locationPredictions}
              name="location"
              placeholder="Search location"
              useDarkText
              // Focusing clears the prefilled location (restored on click-away) -- no manual
              // deleting first, and the "Current location" suggestion is immediately available.
              clearOnFocus
              // Default predictions = ONLY the "Current location" suggestion (the default-searches
              // list is empty) -- lets the user auto-detect their position (client request).
              useDefaultPredictions={true}
              format={identity}
              valueFromForm={values.location}
            />
          </div>
          <button type="submit" className={css.searchButton}>
            <span>Search</span>
            <IconSearch className={css.searchButtonIcon} />
          </button>
        </Form>
      )}
    />
  );
};

export default TopbarSearchPanel;
